import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import type { BarChartConfig, ColumnarTable } from "@stockline/shared/types/bi";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/shadcn/chart";
import {
  toChartNumber,
  isNumericCell,
} from "@stockline/shared/validation/bi/numeric";
import {
  makeCategoryFormatter,
  formatValue,
  formatDelta,
  deltaSign,
  computeCategoryProgress,
  prettifyColumn,
} from "./format";
import { truncateTick } from "./truncate";
import type { ChartVariant } from "./types";
import { cn } from "@/lib/utils";

interface BarChartRendererProps {
  table: ColumnarTable;
  config: BarChartConfig;
  title?: string;
  /** Fixed pixel height for the "default" (chat) variant, whose parent has no
   *  definite height. Ignored by the "card" variant, which fills its card. */
  height?: number;
  /** "card" drops axis-name labels and tightens margins for dashboard cards,
   *  and fills the card body (the card owns the height) so resizing reflows the
   *  chart instead of cropping it; "default" keeps the full chat layout. */
  variant?: ChartVariant;
  /** Enter animation — off for an off-screen export capture so html2canvas
   *  rasterizes the final bars, not a mid-animation frame. */
  animate?: boolean;
}

export interface BarProgressPillProps {
  x?: number;
  y?: number;
  width?: number;
  index?: number;
  /** Compact sizing to match the surrounding card/default convention. */
  card?: boolean;
  /** One entry per category, aligned by index with the chart's data rows —
   *  `null` renders the muted placeholder instead of a coloured percentage. */
  progressByIndex?: (number | null)[] | null;
}

/**
 * Bar `label` renderer: a small pill centered above the bar it's attached to
 * (recharts calls this once per rendered bar, passing that bar's own
 * x/y/width — `y` is already the bar's TOP edge for a positive value). Only
 * meaningful on the "current" series' bar in a 2-series comparison — the
 * pill shows how that category's current value moved vs. its comparison.
 */
export function BarProgressPill({
  x = 0,
  y = 0,
  width = 0,
  index,
  card,
  progressByIndex,
}: Readonly<BarProgressPillProps>) {
  if (!progressByIndex || typeof index !== "number") return null;
  const pct = progressByIndex[index] ?? null;
  const sign = pct === null ? 0 : deltaSign(pct);

  // Match the bar's own rendered width instead of a fixed size, so the pill
  // reads as "belonging to" that specific bar — but never shrink below a
  // floor a signed percentage still fits in comfortably (e.g. "−100.0%"),
  // for a thin bar in a chart with many categories.
  const pillWidth = Math.max(width, card ? 40 : 46);
  const pillHeight = card ? 16 : 18;
  const cx = x + width / 2;
  // Small fixed gap above the bar's top edge so the pill never touches it.
  const cy = y - pillHeight / 2 - 6;

  return (
    <g>
      <rect
        x={cx - pillWidth / 2}
        y={cy - pillHeight / 2}
        width={pillWidth}
        height={pillHeight}
        rx={pillHeight / 2}
        className={cn(
          sign > 0 && "fill-emerald-100 dark:fill-emerald-900/40",
          sign < 0 && "fill-red-100 dark:fill-red-900/40",
          sign === 0 && "fill-muted",
        )}
      />
      <text
        x={cx}
        y={cy}
        dy="0.32em"
        textAnchor="middle"
        fontSize={card ? 9 : 10}
        className={cn(
          "font-medium",
          sign > 0 && "fill-emerald-700 dark:fill-emerald-400",
          sign < 0 && "fill-red-700 dark:fill-red-400",
          sign === 0 && "fill-muted-foreground",
        )}
      >
        {pct === null ? "—" : formatDelta(pct, "%")}
      </text>
    </g>
  );
}

export function BarChartRenderer({
  table,
  config,
  title,
  height = 300,
  variant = "default",
  animate = true,
}: BarChartRendererProps) {
  const { t } = useTranslation();

  const xIdx = table.columns.indexOf(config.x_column);
  const yIdx = table.columns.indexOf(config.y_column);

  // `y_columns` (when it carries ≥2 entries) is authoritative for a grouped/
  // comparison bar chart — mirrors LineChartRenderer's multi-series handling.
  // Fewer than 2 entries isn't a valid multi-series config (the schema
  // enforces `.min(2)`, but we stay defensive), so it falls back to the
  // single `y_column` path below, unchanged.
  const hasMultiSeries = (config.y_columns?.length ?? 0) >= 2;
  const yIndices = hasMultiSeries
    ? config.y_columns!.map((col, i) => ({
        key: `series-${i}`,
        label: prettifyColumn(col),
        idx: table.columns.indexOf(col),
      }))
    : null;

  // No rows, OR a configured column no longer exists in the result (name
  // drift → `indexOf` is -1). Either way there is nothing meaningful to plot,
  // so show the placeholder instead of a chart that silently maps every point
  // to "" / zeroes every series.
  const seriesInvalid = yIndices
    ? yIndices.some(({ idx }) => idx === -1)
    : yIdx === -1;

  if (!table.data || table.data.length === 0 || xIdx === -1 || seriesInvalid) {
    return (
      <div className="text-muted-foreground text-sm p-4">
        {t("charts.noData", "No data to display")}
      </div>
    );
  }

  const card = variant === "card";
  const xLabel = prettifyColumn(config.x_column);
  const showLegend = !!yIndices; // ≥2 series by construction of hasMultiSeries

  const formatName = makeCategoryFormatter(table.data.map((row) => row[xIdx]));

  const yLabel = yIndices ? undefined : prettifyColumn(config.y_column);

  const chartData = yIndices
    ? table.data.map((row) => {
        const point: Record<string, string | number | null> = {
          name: formatName(row[xIdx]),
        };
        for (const { key, idx } of yIndices) {
          point[key] = toChartNumber(row[idx]);
        }
        return point;
      })
    : table.data.map((row) => ({
        name: formatName(row[xIdx]),
        value: toChartNumber(row[yIdx]),
      }));

  const chartConfig = yIndices
    ? (Object.fromEntries(
        yIndices.map(({ key, label }, i) => [
          key,
          { label, color: `hsl(var(--chart-${(i % 5) + 1}))` },
        ]),
      ) satisfies ChartConfig)
    : ({
        value: { label: yLabel, color: "hsl(var(--chart-1))" },
      } satisfies ChartConfig);

  // `show_progress` only makes sense for the "current vs comparison" case —
  // exactly 2 series. Anything else (0/1/3+ series) renders no badge.
  const showProgress =
    !!yIndices && yIndices.length === 2 && !!config.show_progress;

  const progressByIndex: (number | null)[] | null = showProgress
    ? table.data.map((row) => {
        const comparisonCell = row[yIndices![1].idx];
        // A missing (null) or genuinely non-numeric comparison cell can't
        // support a ratio — render the muted placeholder, never NaN/Infinity.
        if (comparisonCell === null || !isNumericCell(comparisonCell)) {
          return null;
        }
        const comparison = toChartNumber(comparisonCell);
        const current = toChartNumber(row[yIndices![0].idx]);
        return computeCategoryProgress(current, comparison);
      })
    : null;

  // The progress pill sits ABOVE the bars, not in the x-axis area, so it
  // needs extra TOP margin (room for the pill above the tallest bar) rather
  // than extra bottom room — the rotated-label footprint is unchanged.
  // A bar sitting at (or near) the y-axis max has its top edge right at the
  // plot area's top boundary, so its pill draws entirely inside this margin —
  // it needs room for its own height + the gap above the bar. The legend is
  // NOT part of this budget: it floats as an absolutely-positioned HTML badge
  // OUTSIDE recharts' margin system entirely (see below), so it never
  // competes with a pill for the same vertical band no matter which bar is
  // tallest.
  // Even WITHOUT a pill, `margin.top` must clear half the topmost y-axis tick
  // label: recharts centres that label on the top gridline (drawn `margin.top`
  // below the plot's top edge), so too small a value leaves the label flush
  // against the card header and it reads as cropped — most visible on an
  // all-negative single-series chart whose top tick is its highest value.
  const topMargin = card ? (showProgress ? 24 : 16) : showProgress ? 32 : 20;

  return (
    <div className="h-full space-y-2">
      {/* The card variant fills its height and never shows its own title (the
          WidgetCard header owns it); rendering one here would both duplicate
          the title and overflow the h-full chart div below. */}
      {title && !card && <h4 className="font-medium text-sm">{title}</h4>}
      <div
        className={cn(
          "relative",
          card ? "h-full w-full" : "w-full",
          // Bars grow FROM THE AXIS (bottom→up clip per bar, staggered L→R)
          // while the axes blur in — data animates, frame stays put. `animate`
          // is isNew → fires on creation AND update (both mark the widget
          // unseen), never on a dashboard-open of an already-seen widget.
          animate && "animate-bars-grow animate-axes-in",
        )}
        style={card ? undefined : { height }}
      >
        {/* Floating corner legend — deliberately OUTSIDE recharts' own
            <Legend>/margin system (unlike LineChartRenderer's in-plot
            legend): a progress pill can sit above ANY bar, including one at
            the y-axis max whose top edge is right at the plot's top edge, so
            an in-margin legend and a tall bar's pill compete for the exact
            same band. This floating HTML badge is a separate layer that
            never consumes any of the plot's reserved margin. `top-1 right-1`
            keeps it inside this chart's own body — it can't reach the
            WidgetCard header row above (title / refresh / delete…), which is
            a separate flex row entirely. */}
        {showLegend && (
          <div
            data-testid="chart-legend-badge"
            className="absolute right-1 top-1 z-10 flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md bg-background/85 px-2 py-1 text-xs backdrop-blur-sm"
          >
            {yIndices!.map(({ key, label }, i) => (
              <span key={key} className="flex items-center gap-1.5">
                {/* The SAME `hsl(var(--chart-N))` formula used to build
                    `chartConfig` above — NOT `var(--color-${key})`. That
                    custom property is scoped by ChartContainer/ChartStyle to
                    `[data-chart=...] *`, and this badge is a SIBLING of
                    ChartContainer (it floats outside its margin system, see
                    above), not a descendant, so the scoped variable never
                    resolves here. `--chart-N` itself is a GLOBAL token from
                    globals.css, so referencing it directly works from
                    anywhere in the DOM. */}
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                  style={{
                    backgroundColor: `hsl(var(--chart-${(i % 5) + 1}))`,
                  }}
                />
                <span className="font-medium text-muted-foreground">
                  {label}
                </span>
              </span>
            ))}
          </div>
        )}
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-full w-full"
        >
          <BarChart
            data={chartData}
            // Keep each category's bar PAIR reading as ONE unit. `barGap` is
            // the small IN-pair gap (the two bars of a pair sit ~2px apart).
            // `barCategoryGap` is the padding recharts reserves on each SIDE
            // of the pair inside its category band — and it also drives where
            // the bars land, via the per-bar "slot" recharts lays out:
            //   slot = (bandSize − 2·barCategoryGap − barGap) / 2
            // Each bar is drawn (capped at `maxBarSize`) CENTERED in its slot.
            // The trap: with FEW categories on a WIDE card the band is huge, so
            // a small FIXED `barCategoryGap` leaves a slot far wider than
            // `maxBarSize` — the two capped bars then float centered in their
            // oversized slots and drift APART (the reported "not close" bug).
            // A PERCENT `barCategoryGap` makes the slot scale WITH the band, so
            // it stays ≈ `maxBarSize` and the bars fill it → the pair hugs
            // (only `barGap` between them) whether there are 4 wide categories
            // or 10 narrow ones. The leftover width becomes BETWEEN-group
            // whitespace, which correctly separates one category's pair from
            // the next. Single-series charts keep recharts' own defaults
            // (`undefined` — no `y_columns`, no pairs to group).
            barGap={hasMultiSeries ? 2 : undefined}
            barCategoryGap={hasMultiSeries ? "30%" : undefined}
            margin={
              // Rotated, all-categories x-axis labels need vertical room below
              // the plot so they aren't clipped. The card variant keeps a
              // tighter footprint than the expanded/default one. `top` grows
              // when the progress pill is shown, so it never clips against
              // the container's top edge above the tallest bar.
              card
                ? { top: topMargin, right: 12, left: 0, bottom: 28 }
                : { top: topMargin, right: 30, left: 20, bottom: 56 }
            }
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={3}
              // Render EVERY category (interval=0) and angle the labels so long
              // product names don't collide or get silently dropped. `height`
              // must clear the diagonal extent of a 16-char label at -45° or the
              // labels spill past the card's bottom edge (overflow-hidden clips
              // them); the bottom margin adds a little breathing room below.
              interval={0}
              angle={-45}
              textAnchor="end"
              height={card ? 72 : 96}
              tick={{ fontSize: card ? 10 : 11 }}
              tickFormatter={(value: string) => truncateTick(value)}
              label={
                card || !xLabel
                  ? undefined
                  : { value: xLabel, position: "bottom", offset: 8 }
              }
            />
            <YAxis
              // A unit prefix widens every tick (e.g. "kg1.2K"), so give the
              // card-mode axis a little more room to avoid clipping into the plot.
              // The default branch MUST stay a concrete number (recharts' own
              // default is 60) — NEVER `undefined`. recharts builds the axis map
              // as `{...YAxis.defaultProps, ...child.props}`, and React 19's JSX
              // runtime no longer drops an explicit `undefined`, so `width:
              // undefined` clobbers the default 60 → `offset.left` becomes NaN →
              // the whole plot width is NaN and nothing renders.
              width={card ? (config.unit ? 52 : 40) : 60}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: card ? 11 : 12 }}
              tickFormatter={(v: number) =>
                formatValue(v, config.unit, { compact: true })
              }
              label={
                card || showLegend || !yLabel
                  ? undefined
                  : { value: yLabel, angle: -90, position: "insideLeft" }
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  valueFormatter={(v) => formatValue(Number(v), config.unit)}
                />
              }
            />
            {yIndices ? (
              // Recharts draws bars in JSX/children order, left to right
              // within each category's band. `yIndices[0]` is always the
              // CURRENT period (drives the progress-pill math above) — but
              // visually the more recent bar belongs on the RIGHT, so render
              // in REVERSE array order while keeping `i` bound to the
              // ORIGINAL (semantic) index, so the pill still attaches to the
              // current-period bar regardless of where it lands on screen.
              [...yIndices]
                .map((s, i) => ({ ...s, i }))
                .reverse()
                .map(({ key, label, i }) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={label}
                    fill={`var(--color-${key})`}
                    radius={[4, 4, 0, 0]}
                    // Wider cap than the single-bar case (64 vs 48): a
                    // grouped PAIR needs to fill more of its category band,
                    // or the two (capped-thin) bars float in a sea of empty
                    // padding — the very "spread out" look this whole gap
                    // tuning is meant to avoid. Still capped so a chart with
                    // very few categories doesn't render comically wide slabs.
                    maxBarSize={64}
                    // Grow disabled: the left→right clip reveal on the wrapper
                    // is the entrance, so bars appear at full height as the wipe
                    // reaches them rather than all rising together.
                    isAnimationActive={false}
                    activeBar={{ fillOpacity: 0.85, strokeWidth: 2 }}
                    // The progress pill is attached ONLY to the "current"
                    // (original index 0) series' bar — it's the one the
                    // comparison is measuring FROM, so the pill belongs
                    // above it, not the comparison bar next to it.
                    label={
                      showProgress && i === 0 ? (
                        <BarProgressPill
                          card={card}
                          progressByIndex={progressByIndex}
                        />
                      ) : undefined
                    }
                  />
                ))
            ) : (
              <Bar
                dataKey="value"
                name={yLabel}
                fill="var(--color-value)"
                radius={[4, 4, 0, 0]}
                // Cap bar thickness to a medium width so a wide chart with few
                // categories doesn't render huge slab-like bars. The category band
                // width is unchanged (recharts still spaces categories the same) —
                // only the drawn bar is capped, centered within its band.
                maxBarSize={48}
                // Grow disabled — the wrapper's left→right clip reveal is the
                // entrance (see the multi-series Bar above).
                isAnimationActive={false}
                activeBar={{ fillOpacity: 0.85, strokeWidth: 2 }}
              />
            )}
          </BarChart>
        </ChartContainer>
      </div>
    </div>
  );
}
